import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { PasswordInput } from "components/ui/password-input";
import { Label } from "components/ui/label";
import { Loader2 } from "lucide-react";
import { validateEmail, required, validateForm as runFormValidation } from "lib/validation";
import { api, extractAuthPayload, isSuccessResponse } from "lib/api";
import { apiURl } from "store/actions";
import { SetAuthUserData } from "store/reducer";
import swtAlert from "lib/sweetAlert";
import { getPostAuthRedirectPath } from "lib/authRedirect";

const formRowClass = "grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4 items-start";

export default function SignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const redirectAfterSignIn = getPostAuthRedirectPath(location.state?.from);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = runFormValidation(formData, {
      email: (v) => validateEmail(v),
      password: required("Password"),
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const resp = await api.post(
        apiURl.login,
        {
          email: formData.email.trim(),
          password: formData.password,
        },
        { auth: false }
      );

      if (!isSuccessResponse(resp)) {
        swtAlert.error(
          "Sign In Failed",
          resp?.message || "Invalid email or password"
        );
        return;
      }

      const auth = extractAuthPayload(resp);
      dispatch(SetAuthUserData(auth));

      swtAlert.success(
        "Signed In",
        resp?.message || "Logged in successfully!"
      );
      navigate(auth.token ? redirectAfterSignIn : "/sign-in", { replace: true });
    } catch (err) {
      swtAlert.error(
        "Sign In Failed",
        err?.message || "Sign in failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  return (
    <div className="container mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-12 xl:px-16">
      <h1 className="text-2xl font-semibold text-center mb-2">Welcome Back</h1>
      <p className="text-center text-sm text-muted-foreground mb-8">
        Sign in to access your investment portfolio
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className={formRowClass}>
          <Label htmlFor="email" className="md:text-right md:pt-3">
            Email
          </Label>
          <div>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleInputChange}
              aria-invalid={!!errors.email}
            />
            {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
          </div>
        </div>

        <div className={formRowClass}>
          <Label htmlFor="password" className="md:text-right md:pt-3">
            Password
          </Label>
          <div>
            <PasswordInput
              id="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleInputChange}
              aria-invalid={!!errors.password}
            />
            <div className="mt-1 flex items-center justify-between gap-3">
              {errors.password ? (
                <p className="text-xs text-destructive">{errors.password}</p>
              ) : (
                <span />
              )}
              <Link
                to="/forgot-password"
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Forgot password?
              </Link>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4 pt-4">
          <div></div>
          <Button type="submit" disabled={isLoading} className="w-full md:w-auto">
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing In...
              </>
            ) : (
              "Log In"
            )}
          </Button>
        </div>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-8">
        Don&apos;t have an account?{" "}
        <Link to="/sign-up" className="text-accent hover:underline font-medium">
          Sign up
        </Link>
      </p>
    </div>
  );
}